import { useState, useMemo } from 'react';
import {
  Box, Typography, Breadcrumbs, Table, TableHead, TableBody,
  TableRow, TableCell, Checkbox, IconButton, Tooltip, TextField,
  InputAdornment, Alert, Stack, alpha, useTheme
} from '@mui/material';

import {
  Folder, HomeRounded, RefreshRounded, SearchRounded,
  VisibilityRounded, FileDownloadRounded,
  LinkRounded, MoreVertRounded, WarningAmberRounded,
  InsertDriveFileRounded, FolderZipRounded, PictureAsPdfRounded,
  ImageRounded, CodeRounded, DescriptionRounded, VideoFileRounded,
  LightModeRounded, DarkModeRounded, FolderOpenRounded,
  ChevronRightRounded
} from '@mui/icons-material';


export function buildVirtualTree(s3Objects) {
  const nodes = {};
  const ensure = (path) => {
    if (!nodes[path]) {
      const segs = path.split('/').filter(Boolean);
      nodes[path] = { name: segs[segs.length - 1] || 'Root', path, children: {}, files: [], lastModified: null, readOnly: false };
    }
    return nodes[path];
  };
  ensure('');
  s3Objects.forEach((obj) => {
    const isExplicitFolder = obj.key.endsWith('/');
    const cleanKey = obj.key.replace(/\/$/, '');
    const segs = cleanKey.split('/').filter(Boolean);
    const folderDepth = isExplicitFolder ? segs.length : segs.length - 1;
    for (let i = 1; i <= folderDepth; i++) ensure(segs.slice(0, i).join('/'));
  });
  s3Objects.forEach((obj) => {
    const isExplicitFolder = obj.key.endsWith('/');
    const cleanKey = obj.key.replace(/\/$/, '');
    const segs = cleanKey.split('/').filter(Boolean);
    if (isExplicitFolder || nodes[cleanKey]) {
      const folder = ensure(cleanKey);
      if (obj.lastModified) folder.lastModified = obj.lastModified;
      if (obj.readOnly) folder.readOnly = true;
      return;
    }
    const parentPath = segs.slice(0, -1).join('/');
    ensure(parentPath).files.push({ name: segs[segs.length - 1], size: obj.size || 0, lastModified: obj.lastModified || null, key: obj.key, readOnly: obj.readOnly || false });
  });
  Object.values(nodes).forEach((folder) => {
    if (!folder.path) return;
    const segs = folder.path.split('/');
    const parentPath = segs.slice(0, -1).join('/');
    const parent = nodes[parentPath] ?? nodes[''];
    if (parent && !parent.children[folder.name]) parent.children[folder.name] = folder;
  });
  return nodes[''];
}

export function navigateTo(root, pathArray) {
  return pathArray.reduce((node, seg) => node?.children?.[seg] ?? node, root);
}

export function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

export function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}


const FILE_TYPES = {
  pdf:  { icon: PictureAsPdfRounded, color: '#dc2626' },
  img:  { icon: ImageRounded,        color: '#2563eb' },
  vid:  { icon: VideoFileRounded,    color: '#7c3aed' },
  zip:  { icon: FolderZipRounded,    color: '#d97706' },
  code: { icon: CodeRounded,         color: '#0284c7' },
  doc:  { icon: DescriptionRounded,  color: '#059669' },
  file: { icon: InsertDriveFileRounded, color: '#64748b' },
};

function getFileType(name) {
  const ext = name.split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return 'pdf';
  if (['png', 'jpg', 'jpeg', 'svg', 'gif', 'webp'].includes(ext)) return 'img';
  if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) return 'vid';
  if (['zip', 'rar', 'tar', 'gz', '7z'].includes(ext)) return 'zip';
  if (['js', 'jsx', 'ts', 'tsx', 'json', 'html', 'css'].includes(ext)) return 'code';
  if (['doc', 'docx', 'txt', 'md', 'csv'].includes(ext)) return 'doc';
  return 'file';
}

function FileIconBadge({ name }) {
  const theme = useTheme();
  const typeKey = getFileType(name);
  const type = FILE_TYPES[typeKey];
  const Icon = type.icon;
  const badgeColor = type.color || theme.palette.text.primary;

  return (
    <Box sx={{ 
      width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
      bgcolor: alpha(badgeColor, 0.16),
      border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon sx={{ fontSize: 18, color: badgeColor }} />
    </Box>
  );
}


const ROW_ACTIONS = [
  { icon: FileDownloadRounded, title: 'Download' },
  { icon: VisibilityRounded,   title: 'Preview' },
  { icon: LinkRounded,         title: 'Copy Link' },
];

function RowActions({ visible, onAction, isFolder }) {
  const theme = useTheme();
  const borderColor = theme.palette.divider;
  const bgColor = alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.12 : 0.9);
  const hoverBg = alpha(theme.palette.action.hover, 0.16);
  const iconColor = theme.palette.text.secondary;
  const hoverColor = theme.palette.text.primary;
  const textDisabled = theme.palette.text.disabled;
  const paperBg = alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.08 : 0.9);
  const paperStrongBg = alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.14 : 0.95);
  const actionsToShow = isFolder ? ROW_ACTIONS.filter(a => a.title !== 'Download' && a.title !== 'Preview') : ROW_ACTIONS;
  
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end"
      sx={{ opacity: visible ? 1 : 0, transition: 'opacity 0.2s ease', pointerEvents: visible ? 'auto' : 'none' }}>
      {actionsToShow.map(({ icon: Icon, title }) => (
        <Tooltip key={title} title={title} placement="top" arrow>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onAction?.(title); }}
            sx={{
              width: 28, height: 28, borderRadius: 1.5,
              color: iconColor,
              bgcolor: bgColor,
              border: `1px solid ${borderColor}`,
              '&:hover': { 
                bgcolor: hoverBg, 
                color: hoverColor, 
                borderColor,
              },
              transition: 'all 0.2s ease',
            }}>
            <Icon sx={{ fontSize: 16, color: 'inherit' }} />
          </IconButton>
        </Tooltip>
      ))}
      <IconButton size="small"
        sx={{
          width: 28, height: 28, borderRadius: 1.5,
          color: textDisabled,
          bgcolor: paperBg,
          border: `1px solid ${borderColor}`,
          '&:hover': { bgcolor: paperStrongBg, color: hoverColor },
        }}>
        <MoreVertRounded sx={{ fontSize: 16 }} />
      </IconButton>
    </Stack>
  );
}


export default function S3FileBrowser({ s3Objects = [], rootLabel = 'Shared with Me', onAction, onToggleTheme, height = '100%' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [path, setPath] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [hovered, setHovered] = useState(null);

  const tree = useMemo(() => buildVirtualTree(s3Objects), [s3Objects]);
  const node = useMemo(() => navigateTo(tree, path), [tree, path]);

  const folders = useMemo(() => {
    const all = Object.values(node?.children ?? {}).sort((a, b) => a.name.localeCompare(b.name));
    return filter ? all.filter((f) => f.name.toLowerCase().includes(filter.toLowerCase())) : all;
  }, [node, filter]);

  const files = useMemo(() => {
    const all = [...(node?.files ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    return filter ? all.filter((f) => f.name.toLowerCase().includes(filter.toLowerCase())) : all;
  }, [node, filter]);

  const totalItems = folders.length + files.length;

  const enterFolder = (name) => { setPath([...path, name]); setFilter(''); setSelected(new Set()); };
  const goToIndex   = (i) => { setPath(path.slice(0, i)); setSelected(new Set()); };

  const allIds = useMemo(() => new Set([...folders.map(f => `f:${f.name}`), ...files.map(f => `fi:${f.name}`)]), [folders, files]);
  const toggleAll  = () => setSelected(selected.size === totalItems && totalItems > 0 ? new Set() : new Set(allIds));
  const toggleItem = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };

  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const textDisabled = theme.palette.text.disabled;
  const paperBg = alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.16 : 0.9);
  const paperStrongBg = alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.22 : 0.98);
  const borderColor = theme.palette.divider;
  const accentColor = theme.palette.text.primary;
  const rowHoverBg = alpha(theme.palette.action.hover, 0.12);
  const rowSelBg = alpha(theme.palette.action.selected || theme.palette.text.primary, 0.14);

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height,
      bgcolor: theme.palette.mode === 'dark' ? '#000000' : theme.palette.background.default,
      borderRadius: 2.5,
      border: `1px solid ${borderColor}`,
      overflow: 'hidden',
      backgroundImage: 'none',
      boxShadow: theme.palette.mode === 'dark'
        ? `0 0 0 1px ${alpha(theme.palette.text.primary, 0.08)}, 0 8px 32px ${alpha(theme.palette.text.primary, 0.12)}`
        : `0 0 0 1px ${alpha(theme.palette.divider, 0.08)}, 0 8px 24px ${alpha(theme.palette.divider, 0.08)}`,
      position: 'relative',
    }}>

      <Box  />

      <Box sx={{
        px: 3, py: 2, mt: '2px',
        borderBottom: `1px solid ${borderColor}`,
        backdropFilter: 'blur(12px)',
        bgcolor: paperBg,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
      }}>

        <Breadcrumbs
          separator={<ChevronRightRounded sx={{ fontSize: 14, color: textDisabled }} />}
          sx={{ '& .MuiBreadcrumbs-ol': { alignItems: 'center', flexWrap: 'nowrap' } }}
        >
          <Box
            component="button"
            onClick={() => goToIndex(0)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, background: 'none', border: 'none',
              cursor: 'pointer', px: 1.5, py: 0.75, borderRadius: 1.5,
              color: path.length === 0 ? accentColor : textSecondary,
              transition: 'all 0.2s ease',
              '&:hover': { bgcolor: paperStrongBg, color: accentColor },
            }}
          >
            <HomeRounded sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: '13px', fontWeight: path.length === 0 ? 600 : 500, lineHeight: 1 }}>{rootLabel}</Typography>
          </Box>

          {path.map((seg, i) => (
            <Box key={i} component="button" onClick={() => goToIndex(i + 1)}
              sx={{
                background: 'none', border: 'none', cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1.5,
                color: i === path.length - 1 ? textPrimary : textSecondary,
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: paperStrongBg, color: accentColor },
              }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: i === path.length - 1 ? 600 : 500 }}>{seg}</Typography>
            </Box>
          ))}
        </Breadcrumbs>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search..."
            size="small"
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                fontSize: '13px',
                height: '34px',
                bgcolor: paperBg,
                '& fieldset': { borderColor },
                '&:hover fieldset': { borderColor: textPrimary },
                '&.Mui-focused fieldset': { borderColor: textPrimary, borderWidth: '1px' },
              },
              '& .MuiInputBase-input': { color: textPrimary },
              '& .MuiInputBase-input::placeholder': { color: textSecondary, opacity: 1 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ fontSize: 16, color: textSecondary }} />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title="Refresh">
            <IconButton
              onClick={() => { setSelected(new Set()); setFilter(''); }}
              size="small"
              sx={{
                width: 34, height: 34, borderRadius: 1.5,
                color: textSecondary,
                border: `1px solid ${borderColor}`,
                bgcolor: paperBg,
                '&:hover': { bgcolor: paperStrongBg, color: accentColor, borderColor },
                transition: 'all 0.2s ease',
              }}
            >
              <RefreshRounded sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>

          {onToggleTheme && (
            <Tooltip title={isDark ? 'Light mode' : 'Dark mode'}>
              <IconButton
                onClick={onToggleTheme}
                size="small"
                sx={{
                  width: 34, height: 34, borderRadius: 1.5,
                  color: textSecondary,
                  border: `1px solid ${borderColor}`,
                  bgcolor: paperBg,
                  '&:hover': { 
                    bgcolor: paperStrongBg,
                    color: accentColor,
                    borderColor,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {isDark ? <LightModeRounded sx={{ fontSize: 17, color: 'inherit' }} /> : <DarkModeRounded sx={{ fontSize: 17, color: 'inherit' }} />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {node?.readOnly && (
        <Alert severity="warning" icon={<WarningAmberRounded sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: 0, borderBottom: `1px solid ${borderColor}`,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            color: isDark ? theme.palette.warning.light : theme.palette.warning.dark,
            fontSize: '13px', py: 0.75, px: 3,
            '& .MuiAlert-icon': { color: theme.palette.warning.main },
          }}>
          View-only access to this folder
        </Alert>
      )}

      <Box sx={{ 
        flex: 1, overflow: 'auto', 
        '&::-webkit-scrollbar': { width: 6, height: 6 }, 
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' }, 
        '&::-webkit-scrollbar-thumb': { bgcolor: theme.palette.divider, borderRadius: 3 } 
      }}>
        <Table stickyHeader sx={{ minWidth: 600,tableLayout:'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 44, bgcolor: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(8px)', borderBottom: `1px solid ${borderColor}` }}>
                <Checkbox size="small"
                  checked={selected.size === totalItems && totalItems > 0}
                  indeterminate={selected.size > 0 && selected.size < totalItems}
                  onChange={toggleAll}
                  sx={{ 
                    p: 0.5,
                    color: theme.palette.text.disabled,
                    '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: accentColor },
                  }}
                />
              </TableCell>
              {['Name','Release Notes', 'Modified', 'Size', 'Downloads'].map((label, i) => (
                <TableCell key={label || i}
                  align={i === 4? 'right' : 'left'}
                  sx={{
                    bgcolor: alpha(theme.palette.background.paper, 0.9),
                    backdropFilter: 'blur(8px)',
                    borderBottom: `1px solid ${borderColor}`,
                    color: theme.palette.text.disabled,
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    //width: i === 1 ? 160 : i === 2 ? 100 : i === 3 ? 180 : 'auto',
                   width:
                    i === 0 ? 250 :
                    i === 1 ? 300 :
                    i === 2 ? 150 :
                    i === 3 ? 100 :
                    120,
                    py: 1.5,
                  }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {folders.map((folder) => {
              const id = `f:${folder.name}`;
              const isSelected = selected.has(id);
              const isHovered = hovered === id;
              return (
                <TableRow key={folder.name}
                  onMouseEnter={() => setHovered(id)}
                  onMouseLeave={() => setHovered(null)}
                  onDoubleClick={() => enterFolder(folder.name)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: isSelected ? rowSelBg : isHovered ? rowHoverBg : 'transparent',
                    transition: 'background-color 0.12s',
                    '& td': { borderBottom: `1px solid ${borderColor}`, py: 1.25 },
                    '& td:first-of-type': {
                      borderLeft: isSelected ? `2px solid ${accentColor}` : '2px solid transparent',
                      transition: 'border-left-color 0.15s',
                    },
                  }}>
                  <TableCell padding="checkbox" sx={{ width: 44 }}>
                    <Checkbox size="small" checked={isSelected}
                      onClick={(e) => e.stopPropagation()} onChange={() => toggleItem(id)} 
                      sx={{ p: 0.5, color: theme.palette.text.disabled, '&.Mui-checked': { color: accentColor } }} />
                  </TableCell>
                  <TableCell onClick={() => enterFolder(folder.name)}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {isHovered
                          ? <FolderOpenRounded sx={{ fontSize: 18, color: accentColor }} />
                          : <Folder sx={{ fontSize: 18, color: accentColor }} />}
                      </Box>
                      <Typography sx={{ fontSize: '13.5px', fontWeight: 500, color: theme.palette.text.primary, letterSpacing: '-0.01em' }}>
                        {folder.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: '12.5px', color: theme.palette.text.disabled, fontFamily: 'monospace' }}>—</TableCell>
                  <TableCell sx={{ fontSize: '12.5px', color: theme.palette.text.disabled, fontFamily: 'monospace' }}>—</TableCell>
                  <TableCell sx={{ fontSize: '12.5px', color: theme.palette.text.disabled, fontFamily: 'monospace' }}>—</TableCell>
                  <TableCell align="right">
                    <RowActions visible={isHovered || isSelected} onAction={(a) => onAction?.(a, folder)} isFolder />
                  </TableCell>
                  
                </TableRow>
              );
            })}

            {files.map((file) => {
              const id = `fi:${file.name}`;
              const isSelected = selected.has(id);
              const isHovered = hovered === id;
              return (
                <TableRow key={file.name}
                  onMouseEnter={() => setHovered(id)}
                  onMouseLeave={() => setHovered(null)}
                  sx={{
                    bgcolor: isSelected ? rowSelBg : isHovered ? rowHoverBg : 'transparent',
                    transition: 'background-color 0.12s',
                    '& td': { borderBottom: `1px solid ${borderColor}`, py: 1.25 },
                    '& td:first-of-type': {
                      borderLeft: isSelected ? `2px solid ${accentColor}` : '2px solid transparent',
                      transition: 'border-left-color 0.15s',
                    },
                  }}>
                  <TableCell padding="checkbox" sx={{ width: 44 }}>
                    <Checkbox size="small" checked={isSelected}
                      onClick={(e) => e.stopPropagation()} onChange={() => toggleItem(id)} 
                      sx={{ p: 0.5, color: theme.palette.text.disabled, '&.Mui-checked': { color: accentColor } }} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <FileIconBadge name={file.name} />
                      <Typography sx={{ fontSize: '13.5px', fontWeight: 400, color: theme.palette.text.primary, letterSpacing: '-0.01em' }}>
                        {file.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: '12.5px', color: theme.palette.text.secondary, fontFamily: 'monospace' }}>{formatDate(file.lastModified)}</TableCell>
                  <TableCell sx={{ fontSize: '12.5px', color: theme.palette.text.secondary, fontFamily: 'monospace' }}>{formatFileSize(file.size)}</TableCell>
                  <TableCell align="right">
                    <RowActions visible={isHovered || isSelected} onAction={(a) => onAction?.(a, file)} />
                  </TableCell>
                </TableRow>
              );
            })}

            {totalItems === 0 && (
              <TableRow>
                <TableCell colSpan={5} sx={{ borderBottom: 'none', py: 10 }}>
                  <Stack alignItems="center" spacing={2}>
                    <Box sx={{
                      width: 56, height: 56, borderRadius: 2.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FolderOpenRounded sx={{ fontSize: 26, color: accentColor, opacity: 0.9 }} />
                    </Box>
                    <Box textAlign="center">
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                        {filter ? 'No results found' : 'This folder is empty'}
                      </Typography>
                      <Typography sx={{ fontSize: '12.5px', color: theme.palette.text.disabled }}>
                        {filter ? `Nothing matches "${filter}"` : 'No files or folders here yet.'}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{
        px: 3, py: 1.5,
        borderTop: `1px solid ${borderColor}`,
        bgcolor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Typography sx={{ fontSize: '12px', color: theme.palette.text.disabled, fontFamily: 'monospace' }}>
          {selected.size > 0 ? `${selected.size} selected` : `${totalItems} item${totalItems !== 1 ? 's' : ''}`}
        </Typography>

        {selected.size > 0 && (
          <Box sx={{
            px: 1.5, py: 0.4, borderRadius: '20px',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}>
            <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: accentColor, letterSpacing: '0.02em' }}>
              {selected.size} selected
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}